/**
 * Session steps are generic: an event type from any module, shared defaults and per-animal fields.
 * These helpers turn a step plus what was entered for one animal into event data, and work out
 * what is still missing. Registry lookups are passed in so this stays pure.
 */
import { fieldZod, stripNulls, validateFields, type Basis, type EventTypeDef, type Field, type Issue } from "../../../contract";
import type { SessionSetup, SortRule, StepDef } from "./types";

export type Lookup = (type: string) => EventTypeDef | undefined;

/** The role an animal plays in this event type, or null when it isn't about one animal. */
export function animalRole(def: EventTypeDef | undefined): string | null {
  return def?.subjects.find((s) => s.kind === "animal")?.role ?? null;
}

/** A step shows at the chute only when its record type exists on this ranch and is about an animal. */
export function stepAvailable(step: StepDef, lookup: Lookup): boolean {
  const def = lookup(step.type);
  return !!def && !!animalRole(def);
}

export function splitSteps(steps: StepDef[], lookup: Lookup): { visible: StepDef[]; hidden: StepDef[] } {
  const visible: StepDef[] = [];
  const hidden: StepDef[] = [];
  for (const s of steps) (stepAvailable(s, lookup) ? visible : hidden).push(s);
  return { visible, hidden };
}

/**
 * A step as the chute uses it: any required field that has neither a shared value nor a per-animal
 * input gets an input, so a step can never be impossible to save.
 */
export function withRequiredEntry(step: StepDef, def: EventTypeDef): StepDef {
  const missing = Object.entries(def.fields).filter(([k, f]) => f.required && !step.entry.includes(k) && !filled(step.defaults?.[k])).map(([k]) => k);
  return missing.length ? { ...step, entry: [...step.entry, ...missing] } : step;
}

/** Per-animal fields that exist on the record type, in the step's order. */
export function entryFields(step: StepDef, def: EventTypeDef): Array<[string, Field]> {
  return step.entry.filter((k) => def.fields[k]).map((k) => [k, def.fields[k]]);
}

/** Shared defaults that exist on the record type. Unknown keys are dropped, never sent. */
export function knownDefaults(step: StepDef, def: EventTypeDef): Record<string, unknown> {
  return Object.fromEntries(Object.entries(step.defaults ?? {}).filter(([k]) => def.fields[k] && !step.entry.includes(k)));
}

/** Event data for one animal: shared defaults, then what was entered. Empty values are omitted. */
export function stepData(step: StepDef, def: EventTypeDef, entered: Record<string, unknown>): Record<string, unknown> {
  const own = Object.fromEntries(Object.entries(entered ?? {}).filter(([k]) => def.fields[k] && step.entry.includes(k)));
  return stripNulls({ ...knownDefaults(step, def), ...own }) as Record<string, unknown>;
}

/** Where each value came from, so history shows which facts were session defaults. */
export function stepEvidence(step: StepDef, def: EventTypeDef, entered: Record<string, unknown>): Record<string, Basis> {
  const out: Record<string, Basis> = {};
  for (const k of Object.keys(stepData(step, def, entered))) out[k] = step.entry.includes(k) ? "stated" : "default";
  return out;
}

function filled(v: unknown): boolean {
  if (v === undefined || v === null || v === "") return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "object" && "value" in (v as Record<string, unknown>)) return typeof (v as { value: unknown }).value === "number";
  return true;
}

/** Required fields with no value yet for this animal. */
export function missingFields(step: StepDef, def: EventTypeDef, entered: Record<string, unknown>): string[] {
  const data = stepData(step, def, entered);
  return Object.entries(def.fields).filter(([k, f]) => f.required && !filled(data[k])).map(([k]) => k);
}

/** Field-level problems with what was entered (wrong shape, out of range). */
export function stepIssues(step: StepDef, def: EventTypeDef, entered: Record<string, unknown>): Issue[] {
  const checked = validateFields(def.fields, stepData(step, def, entered));
  return checked.ok ? [] : checked.issues;
}

export function isWeightField(f: Field | undefined): boolean {
  return !!f && f.kind === "quantity" && f.dimension === "weight";
}

/** The first per-animal weight field, which the scale fills. */
export function weightTarget(steps: StepDef[], lookup: Lookup): { step_id: string; field: string } | null {
  for (const s of steps) {
    const def = lookup(s.type);
    if (!def || !animalRole(def)) continue;
    const key = s.entry.find((k) => isWeightField(def.fields[k]));
    if (key) return { step_id: s.id, field: key };
  }
  return null;
}

/** Fields to enter per animal when a step is first added: what's required, plus weights. */
export function suggestEntry(def: EventTypeDef): string[] {
  return Object.entries(def.fields).filter(([, f]) => f.required || isWeightField(f)).map(([k]) => k);
}

/** Problems with a step as configured in a template (for the editor and the server). */
export function checkStep(step: StepDef, lookup: Lookup): Issue[] {
  const def = lookup(step.type);
  if (!def) return [{ path: `steps.${step.id}.type`, message: "That kind of record isn't available on this ranch yet." }];
  const issues: Issue[] = [];
  if (!animalRole(def)) issues.push({ path: `steps.${step.id}.type`, message: "This record isn't about one animal." });
  for (const k of step.entry) if (!def.fields[k]) issues.push({ path: `steps.${step.id}.entry.${k}`, message: "Unknown field" });
  for (const [k, v] of Object.entries(step.defaults ?? {})) {
    const field = def.fields[k];
    if (!field) { issues.push({ path: `steps.${step.id}.defaults.${k}`, message: "Unknown field" }); continue; }
    const parsed = fieldZod({ ...field, required: false }).safeParse(stripNulls(v));
    if (!parsed.success) issues.push({ path: `steps.${step.id}.defaults.${k}`, message: parsed.error.issues[0]?.message ?? "Invalid" });
  }
  for (const [k, f] of Object.entries(def.fields)) {
    if (f.required && !step.entry.includes(k) && !filled(step.defaults?.[k])) issues.push({ path: `steps.${step.id}.${k}`, message: "Enter it for each animal or set it for all." });
  }
  return issues;
}

let seq = 0;
export function stepId(): string {
  seq = (seq + 1) % 1000;
  return `s${Date.now().toString(36)}${seq.toString(36)}`;
}

/** A sensible first configuration for a record type: required and weight fields per animal. */
export function newStep(def: EventTypeDef): StepDef {
  return { id: stepId(), type: def.type, defaults: {}, entry: suggestEntry(def) };
}

export interface StepHints {
  stock_item_id?: string;
  stock_lot_id?: string;
  dose?: { value: number; unit: string };
  /** Preferred enum option values, matched case-insensitively against option ids and labels. */
  prefer?: string[];
  /** Fields to always enter per animal. */
  entry?: string[];
}

/**
 * Fill a step's shared defaults from loose hints when the exact field names belong to another
 * module (used for demo templates): a product ref takes the product, a dose quantity takes the
 * dose, an enum takes a preferred option. Every guess is validated; anything that doesn't fit is
 * left out rather than invented.
 */
export function adaptStep(def: EventTypeDef, hints: StepHints, base: Partial<StepDef> = {}): StepDef {
  const asked = (hints.entry ?? []).filter((k) => def.fields[k]);
  const defaults: Record<string, unknown> = {};
  for (const [k, f] of Object.entries(def.fields)) {
    if (asked.includes(k) || isWeightField(f)) continue;
    let v: unknown;
    if (f.kind === "ref" && f.target === "stock_lot" && hints.stock_lot_id) v = f.multiple ? [hints.stock_lot_id] : hints.stock_lot_id;
    else if (f.kind === "ref" && f.target === "stock_item" && hints.stock_item_id) v = f.multiple ? [hints.stock_item_id] : hints.stock_item_id;
    else if (f.kind === "quantity" && (f.dimension === "dose" || f.dimension === "volume") && hints.dose) v = hints.dose;
    else if (f.kind === "enum" && !f.multiple && hints.prefer?.length) {
      const wanted = hints.prefer.map((p) => p.toLowerCase());
      v = f.options.find((o) => wanted.includes(o.toLowerCase()) || wanted.includes((f.optionLabels[o] ?? "").toLowerCase()));
    }
    if (v === undefined) continue;
    if (fieldZod({ ...f, required: false }).safeParse(v).success) defaults[k] = v;
  }
  // Whatever the hints didn't settle and the record needs is entered per animal, plus any weight.
  const entry = [...new Set([...asked, ...suggestEntry(def).filter((k) => !(k in defaults))])];
  return { id: base.id ?? stepId(), type: def.type, ...(base.label ? { label: base.label } : {}), defaults, entry, ...(base.ask ? { ask: true } : {}) };
}

/** Normalize a stored setup so older or agent-created jobs always have the shape the runner expects. */
export function normalizeSetup(data: Record<string, unknown>, fallbackKind: SessionSetup["kind"], batchId: string): SessionSetup {
  const steps = Array.isArray(data.steps) ? (data.steps as StepDef[]).filter((s) => s && typeof s.type === "string").map((s) => ({
    id: String(s.id ?? stepId()), type: s.type, label: typeof s.label === "string" && s.label.trim() ? s.label.trim() : undefined,
    defaults: s.defaults && typeof s.defaults === "object" ? s.defaults : {}, entry: Array.isArray(s.entry) ? s.entry.filter((k) => typeof k === "string") : [],
    ask: !!s.ask, subjects: Array.isArray(s.subjects) ? s.subjects : undefined,
  })) : [];
  return {
    kind: data.kind === "pen" || data.kind === "chute" ? data.kind : fallbackKind,
    steps,
    sort: Array.isArray(data.sort) ? (data.sort as SortRule[]) : [],
    template_id: typeof data.template_id === "string" ? data.template_id : null,
    batch_id: typeof data.batch_id === "string" && data.batch_id ? data.batch_id : batchId,
    source_job_id: typeof data.source_job_id === "string" ? data.source_job_id : null,
    crew: Array.isArray(data.crew) ? (data.crew as string[]) : [],
    worked_on: typeof data.worked_on === "string" && /^\d{4}-\d{2}-\d{2}$/.test(data.worked_on) ? data.worked_on : null,
    notes: typeof data.notes === "string" ? data.notes : null,
    started_at: typeof data.started_at === "string" ? data.started_at : null,
    finished_at: typeof data.finished_at === "string" ? data.finished_at : null,
  };
}
